import React from 'react';
import { motion } from 'framer-motion';
import { FaDatabase, FaTasks, FaBolt, FaServer } from 'react-icons/fa';

const stories = [
  {
    title: 'Database Query Optimization',
    description:
      'Optimized complex MySQL queries and indexing strategies reducing database load times by 60%.',
    techniques: ['Query refactoring', 'Index optimization', 'Database normalization'],
    icon: FaDatabase,
  },
  {
    title: 'Queue-Based Processing',
    description:
      'Implemented Laravel queue workers and Redis queues for background job processing and large workload distribution.',
    techniques: ['Redis queues', 'Job batching', 'Async processing'],
    icon: FaTasks,
  },
  {
    title: 'Caching Strategy',
    description:
      'Implemented Redis caching layers reducing API response time and database load.',
    techniques: ['Redis caching', 'Query result caching', 'Session caching'],
    icon: FaBolt,
  },
  {
    title: 'Scalable SaaS Infrastructure',
    description:
      'Designed backend systems capable of handling thousands of concurrent users.',
    techniques: ['Load balancing', 'Queue workers', 'Cloud storage'],
    icon: FaServer,
  },
];

const PerformanceEngineeringV2 = () => {
  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="w-full rounded-2xl bg-white border border-gray-200 p-6 md:p-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">
          Performance Engineering &amp; Optimization
        </h2>
        <div className="w-16 h-1 bg-blue-600 rounded mt-2 mb-6"></div>
        <p className="text-sm md:text-base text-gray-600 max-w-2xl mb-8">
          Optimization strategies applied to improve latency, throughput, and stability in production backend systems.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {stories.map((story, index) => {
            const Icon = story.icon;
            return (
              <motion.article
                key={story.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                className="rounded-xl bg-gray-50 border border-gray-200 p-6 md:p-8 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 space-y-3"
              >
                <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center mb-4">
                  <Icon className="text-xl text-blue-600" />
                </div>

                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">{story.title}</h3>
                <p className="max-w-prose text-sm md:text-base text-gray-600 leading-relaxed mb-4">{story.description}</p>

                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">Techniques</h4>
                  <div className="flex flex-wrap gap-2">
                    {story.techniques.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
      </div>
    </section>
  );
};

export default PerformanceEngineeringV2;
